import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { eventBus } from '../event-bus';
import type { ModuleStore, ModuleId, ModuleState } from './types';
import type { ModuleMetadata } from '../drawable-types-registry';

/**
 * Estado inicial del store
 */
const initialState: ModuleState = {
  activeModule: 'urban-network', // Por defecto, módulo Red Urbana activo
  availableModules: [],
  modulesMetadata: {} as Record<ModuleId, ModuleMetadata>,
  isLoading: false,
  error: null,
};

/**
 * Module Store con Zustand
 *
 * @remarks
 * Gestiona el estado del módulo activo y los módulos disponibles.
 * Emite eventos cuando cambia el módulo activo para que otros componentes
 * puedan reaccionar (ej: actualizar toolbar, cambiar capas del mapa, etc.).
 *
 * Eventos emitidos:
 * - 'module.active.changed': Cuando cambia el módulo activo
 * - 'module.available.changed': Cuando cambian los módulos disponibles
 *
 * @example
 * ```tsx
 * function ModuleSelector() {
 *   const { activeModule, availableModules, setActiveModule } = useModuleStore();
 *
 *   return (
 *     <select
 *       value={activeModule || ''}
 *       onChange={(e) => setActiveModule(e.target.value || null)}
 *     >
 *       <option value="">Sin módulo</option>
 *       {availableModules.map(moduleId => (
 *         <option key={moduleId} value={moduleId}>
 *           {moduleId}
 *         </option>
 *       ))}
 *     </select>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Escuchar cambios de módulo en otro componente
 * import { eventBus } from '@/core/event-bus';
 *
 * useEffect(() => {
 *   const unsubscribe = eventBus.on('module.active.changed', (payload) => {
 *     console.log('Módulo activo:', payload.moduleId);
 *     console.log('Módulo anterior:', payload.previousModuleId);
 *     // Actualizar UI según el módulo activo
 *   });
 *
 *   return () => unsubscribe();
 * }, []);
 * ```
 */
export const useModuleStore = create<ModuleStore>()(
  devtools(
    (set, get) => ({
      // Estado inicial
      ...initialState,

      // Acciones
      setActiveModule: (moduleId: ModuleId | null): void => {
        const previousModuleId = get().activeModule;

        // No hacer nada si ya está activo
        if (previousModuleId === moduleId) {
          return;
        }

        // Validar que el módulo esté disponible (si no es null)
        if (moduleId !== null && !get().isModuleAvailable(moduleId)) {
          console.warn(
            `[ModuleStore] Module '${moduleId}' is not available. Available modules:`,
            get().availableModules
          );
          set({ error: `El módulo '${moduleId}' no está disponible en su plan` });
          return;
        }

        // Actualizar estado
        set({ activeModule: moduleId, error: null });

        // Emitir evento para que otros componentes reaccionen
        eventBus.emit('module.active.changed', {
          moduleId,
          previousModuleId,
          timestamp: Date.now(),
        });

        console.debug(
          `[ModuleStore] Active module changed: ${previousModuleId || 'none'} → ${moduleId || 'none'}`
        );
      },

      setAvailableModules: (modules: ModuleId[]): void => {
        const previousModules = get().availableModules;

        // No hacer nada si no cambió
        if (
          previousModules.length === modules.length &&
          previousModules.every((m) => modules.includes(m))
        ) {
          return;
        }

        set({ availableModules: modules });

        // Si el módulo activo ya no está disponible, desactivarlo
        const { activeModule } = get();
        if (activeModule && !modules.includes(activeModule)) {
          console.warn(
            `[ModuleStore] Active module '${activeModule}' is no longer available. Deactivating.`
          );
          get().setActiveModule(null);
        }

        // Emitir evento
        eventBus.emit('module.available.changed', {
          modules,
          previousModules,
          timestamp: Date.now(),
        });

        console.debug(
          `[ModuleStore] Available modules changed:`,
          previousModules,
          '→',
          modules
        );
      },

      registerModuleMetadata: (moduleId: ModuleId, metadata: ModuleMetadata): void => {
        // Validar que el moduleId coincida
        if (metadata.id !== moduleId) {
          console.warn(
            `[ModuleStore] Metadata id '${metadata.id}' does not match moduleId '${moduleId}'. Using '${moduleId}'.`
          );
          metadata = { ...metadata, id: moduleId };
        }

        set((state) => ({
          modulesMetadata: {
            ...state.modulesMetadata,
            [moduleId]: metadata,
          },
        }));

        console.debug(`[ModuleStore] Registered metadata for module '${moduleId}'`);
      },

      getModuleMetadata: (moduleId: ModuleId): ModuleMetadata | undefined => {
        return get().modulesMetadata[moduleId];
      },

      isModuleAvailable: (moduleId: ModuleId): boolean => {
        return get().availableModules.includes(moduleId);
      },

      isModuleActive: (moduleId: ModuleId): boolean => {
        return get().activeModule === moduleId;
      },

      setLoading: (isLoading: boolean): void => {
        set({ isLoading });
      },

      setError: (error: string | null): void => {
        set({ error });
      },

      clearError: (): void => {
        set({ error: null });
      },

      reset: (): void => {
        // Emitir evento de desactivación si había módulo activo
        const { activeModule } = get();
        if (activeModule) {
          eventBus.emit('module.active.changed', {
            moduleId: null,
            previousModuleId: activeModule,
            timestamp: Date.now(),
          });
        }

        set(initialState);
        console.debug('[ModuleStore] Reset to initial state');
      },
    }),
    {
      name: 'module-store', // Para Redux DevTools
    }
  )
);

/**
 * Selector para obtener el módulo activo
 */
export const selectActiveModule = (state: ModuleStore): ModuleId | null => state.activeModule;

/**
 * Selector para obtener los módulos disponibles
 */
export const selectAvailableModules = (state: ModuleStore): ModuleId[] =>
  state.availableModules;

/**
 * Selector para verificar si hay un módulo activo
 */
export const selectHasActiveModule = (state: ModuleStore): boolean =>
  state.activeModule !== null;

/**
 * Selector para obtener metadata del módulo activo
 */
export const selectActiveModuleMetadata = (state: ModuleStore): ModuleMetadata | undefined => {
  if (!state.activeModule) return undefined;
  return state.modulesMetadata[state.activeModule];
};
